import { useMemo } from 'react'
import {
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import type { AnimationTiming } from 'recharts/types/util/types'
import { ChartCard } from './ChartCard'
import { CHART_ANIMATION } from '../constants/chartAnimation'
import type { AggregatedChartData } from '../types'

// Felton Chart Colors (RGBA format for SVG alpha support)
const RADAR_COLORS_FILL = [
  'rgba(82, 132, 239, 0.15)',    // chart-1: Indigo
  'rgba(77, 219, 200, 0.15)',    // chart-2: Teal (Ciano)
  'rgba(246, 174, 49, 0.15)',    // chart-3: Amber
  'rgba(245, 86, 113, 0.15)',    // chart-4: Rose
  'rgba(146, 92, 240, 0.15)',    // chart-5: Violet
  'rgba(40, 215, 133, 0.15)',    // chart-6: Emerald
  'rgba(53, 183, 243, 0.15)',    // chart-7: Sky
] as const

const RADAR_COLORS_STROKE = [
  'rgba(82, 132, 239, 0.8)',     // chart-1
  'rgba(77, 219, 200, 0.8)',     // chart-2: Teal (Ciano)
  'rgba(246, 174, 49, 0.8)',     // chart-3
  'rgba(245, 86, 113, 0.8)',     // chart-4
  'rgba(146, 92, 240, 0.8)',     // chart-5
  'rgba(40, 215, 133, 0.8)',     // chart-6
  'rgba(53, 183, 243, 0.8)',     // chart-7
] as const

export interface V11RadarChartProps {
  readonly data: ReadonlyArray<AggregatedChartData>
  readonly title?: string
  readonly subtitle?: string
  readonly colorOffset?: number
  readonly maxItems?: number
}

/**
 * V11 Radar Chart - Multi-dimensional web visualization
 *
 * Spider/web chart for qualitative data (lifestyle, emotion).
 * Shows multi-dimensional profile at a glance.
 *
 * Features:
 * - Smooth polygon with gradient fill
 * - Capsule DNA colors (Felton palette)
 * - Animated entry (700ms bounce)
 * - Top 8 items (optimal for readability)
 */
export const V11RadarChart: React.FC<V11RadarChartProps> = ({
  data,
  title = 'Radar',
  subtitle,
  colorOffset = 0,
  maxItems = 8,
}) => {
  const chartData = useMemo(() => {
    // Sort by value and take top N
    const sorted = [...data]
      .sort((a, b) => b.value - a.value)
      .slice(0, maxItems)

    // Transform to Recharts format
    return sorted.map((item) => ({
      category: item.label,
      value: item.value,
    }))
  }, [data, maxItems])

  // Use colorOffset for variety across different radar charts
  const colorIndex = colorOffset % RADAR_COLORS_FILL.length
  const fillColor = RADAR_COLORS_FILL[colorIndex]
  const strokeColor = RADAR_COLORS_STROKE[colorIndex]

  if (chartData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div className="flex items-center justify-center min-h-[160px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <div className="w-full" style={{ backgroundColor: 'transparent' }}>
        <ResponsiveContainer width="100%" height={320}>
          <RechartsRadarChart
            data={chartData}
            style={{ backgroundColor: 'transparent' }}
            className="[&_svg]:bg-transparent"
          >

          <PolarGrid
            stroke="rgba(148, 163, 184, 0.4)"
            strokeWidth={1}
          />

          <PolarAngleAxis
            dataKey="category"
            stroke="currentColor"
            className="text-v11-text-secondary text-xs"
            tick={{ fontSize: 11, fill: 'currentColor' }}
          />

          <PolarRadiusAxis
            angle={90}
            domain={[0, 'auto']}
            stroke="currentColor"
            className="text-v11-text-muted text-xs"
            tick={{ fontSize: 10 }}
            tickCount={5}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--v11-surface))',
              border: '1px solid hsl(var(--v11-border))',
              borderRadius: '12px',
              padding: '8px 12px',
            }}
            labelStyle={{
              color: 'hsl(var(--v11-text-primary))',
              fontWeight: 600,
              marginBottom: '4px',
            }}
            itemStyle={{
              color: 'hsl(var(--v11-text-secondary))',
            }}
            formatter={(value: number) => value.toLocaleString()}
          />

          <Radar
            name="Profiles"
            dataKey="value"
            stroke={strokeColor}
            fill={fillColor}
            strokeWidth={1}
            fillOpacity={1}
            animationDuration={CHART_ANIMATION.duration}
            animationEasing={CHART_ANIMATION.easing as AnimationTiming}
            isAnimationActive={true}
          />
        </RechartsRadarChart>
      </ResponsiveContainer>
      </div>
    </ChartCard>
  )
}
